import React from 'react'
import { IActions, IActionType } from './ReportPage'
import { H6, Switch } from '@blueprintjs/core'
import { FilterContainer, Flex, DateRangeSelect, MultiEmployeeSelect } from '@ui'
import { Mode } from '@ui/selects/LocationSelect'
import { IReportFilters } from './types'
import { DateRange } from '@blueprintjs/datetime'
import {
  YearSelect,
  MultiSizeSelect,
  MultiCostCenterSelect,
  MultiGlobalMapObjectSelect,
  MultiLocationSelect,
  MultiDepartmentSelect,
  MultiSpaceTypeSelect,
  MultiUsageTypeSelect,
  MultiEmployeeTypeSelect,
  MultiHRLocationSelect,
  MultiRegionSelect,
  MultiCountrySelect,
} from '@ui'
import { createYearOptions } from '@ui/selects/YearSelect'

import track from '@track'
// import moment from 'moment'
import { DateUtil } from '@utils'
import APWidget from 'components/APWidget'

export interface IFilter {
  name: string
  id:
    | 'assignmentPlan'
    | 'departmentId'
    | 'teamId'
    | 'city'
    | 'buildingId'
    | 'floorId'
    | 'floor'
    | 'spaceTypeId'
    | 'usageTypeId'
    | 'employmentType'
    | 'globalMapObjectId'
    | 'site'
    | 'location'
    | 'costCenterId'
    | 'size'
    | 'spaceId'
    | 'search'
    | 'fromDate'
    | 'toDate'
    | 'dateRange'
    | 'isCancelled'
    | 'hiringManager'
    | 'subCostCenterId'
    | 'employeeName'
    | 'employeeId'
}

interface IProps {
  reportName: string
  reportConfig: any
  requiredFilters: IFilter[]
  dispatchFilter: React.Dispatch<IActions>
  reportFilters: IReportFilters
  bottomMargin: boolean
}

export default function ReportFilters({
  reportName,
  reportConfig,
  reportFilters,
  dispatchFilter,
  requiredFilters,
}: IProps) {
  let locationType: 'building' | 'floor' = 'building'

  const onDateRangeChange = React.useCallback(
    (newRange: any) =>
      dispatchFilter({
        type: IActionType.DATE_RANGE_FILTER,
        payload: {
          range: newRange as DateRange,
        },
      }),
    [dispatchFilter]
  )

  const onStartYearChange = React.useCallback(
    (newYear: any) =>
      dispatchFilter({
        type: IActionType.START_YEAR_CHANGE_FILTER,
        payload: {
          startYear: newYear,
        },
      }),
    [dispatchFilter]
  )

  const onEndYearChange = React.useCallback(
    (newYear: any) =>
      dispatchFilter({
        type: IActionType.END_YEAR_CHANGE_FILTER,
        payload: {
          endYear: newYear,
        },
      }),
    [dispatchFilter]
  )

  // remove unused search filter
  const filtersById = requiredFilters.reduce(
    (result: { [id: string]: { name: string } | undefined }, currentFilter) => {
      switch (currentFilter.id) {
        case 'search':
        case 'hiringManager':
        case 'employeeName':
        case 'teamId':
        case 'toDate':
        case 'subCostCenterId':
          return result
        case 'location':
        case 'site':
          return { ...result, site: { name: 'HR Location' } }
        case 'fromDate':
          return { ...result, dateRange: { name: 'Date Range' } }
        case 'isCancelled':
          return { ...result, isCancelled: { name: 'isCancelled' } }
        case 'buildingId':
          locationType = 'building'
          break
        case 'floorId':
        case 'floor':
          locationType = 'floor'
          break
      }
      if (reportConfig.useFloorFilter) locationType = 'floor'

      return { ...result, [currentFilter.id]: { name: currentFilter.name } }
    },
    {
      dateRange: undefined,
      isCancelled: undefined,
      assignmentPlan: undefined,
      region: undefined,
      country: undefined,
      city: undefined,
      buildingId: undefined,
      departmentId: undefined,
      spaceTypeId: undefined,
      usageTypeId: undefined,
      employmentType: undefined,
      globalMapObjectId: undefined,
      site: undefined,
      costCenterId: undefined,
      size: undefined,
      spaceId: undefined,
      calculateForYear: undefined,
      employeeId: undefined,
    }
  )

  const filterFromDate = reportFilters.fromDate
    ? DateUtil.parse(reportFilters.fromDate, 'yyyy-MM-dd')
    : DateUtil()
  const filterToDate = reportFilters.toDate
    ? DateUtil.parse(reportFilters.toDate, 'yyyy-MM-dd')
    : DateUtil()

  const mode = locationType === 'building' ? Mode.MULTI_BUILDING : Mode.MULTI_FLOOR
  return (
    <div>
      <Flex>
        {filtersById.dateRange && (
          <FilterContainer>
            {reportConfig.yearSelector ? (
              <>
                <H6>Start Year</H6>
                <YearSelect
                  options={createYearOptions(2000, 50)}
                  value={
                    reportFilters.fromDate
                      ? filterFromDate.year()
                      : reportConfig.defaultFromYearFilter
                  }
                  onChange={onStartYearChange}
                />
              </>
            ) : (
              <>
                <H6>{filtersById.dateRange.name}</H6>
                <DateRangeSelect
                  past={reportConfig.past}
                  future={reportConfig.future}
                  value={
                    reportFilters.fromDate && reportFilters.toDate
                      ? [filterFromDate.toDate(), filterToDate.toDate()]
                      : reportConfig.defaultDateFilter.dateRange
                  }
                  onChange={onDateRangeChange}
                />
              </>
            )}
          </FilterContainer>
        )}
        {reportConfig.endYear && (
          <FilterContainer>
            <H6>To Year</H6>
            <YearSelect
              options={() => {
                const start = reportFilters.fromDate
                  ? filterFromDate.year()
                  : reportConfig.defaultFromYearFilter
                return createYearOptions(start, 20)
              }}
              value={reportFilters.toDate ? filterToDate.year() : reportConfig.defaultToYearFilter}
              onChange={onEndYearChange}
            />
          </FilterContainer>
        )}
        {filtersById.calculateForYear && (
          <FilterContainer>
            <H6>Calculate Metrics for</H6>
            <YearSelect
              options={() => {
                const tempStart: number = reportFilters.fromDate
                  ? filterFromDate.year()
                  : reportConfig.defaultFromYearFilter
                const tempEnd: number = reportFilters.toDate
                  ? filterToDate.year()
                  : reportConfig.defaultToYearFilter
                return createYearOptions(tempStart, tempEnd - tempStart + 1)
              }}
              value={
                reportFilters.calculateForYear
                  ? reportFilters.calculateForYear
                  : reportFilters.toDate
                  ? filterToDate.year()
                  : reportConfig.defaultToYearFilter
              }
              onChange={(year) =>
                dispatchFilter({
                  type: IActionType.FILTER,
                  payload: {
                    category: 'calculateForYear',
                    value: year,
                  },
                })
              }
            />
          </FilterContainer>
        )}
        {filtersById.isCancelled && (
          <FilterContainer>
            <H6>Show Only Cancelled Bookings</H6>
            <Switch
              checked={reportFilters.isCancelled}
              onChange={(e: any) => {
                dispatchFilter({
                  type: IActionType.FILTER,
                  payload: {
                    category: 'isCancelled',
                    value: e.target.checked,
                  },
                })
              }}
            />
          </FilterContainer>
        )}
        {filtersById.assignmentPlanId && (
          <FilterContainer>
            <H6>Assignment Plan</H6>
            <APWidget
              readonly
              value={(reportFilters.assignmentPlan && reportFilters.assignmentPlan.id) || null}
              onChange={(ap) =>
                dispatchFilter({
                  type: IActionType.FILTER,
                  payload: {
                    category: 'assignmentPlan',
                    value: ap.name === 'Current' ? null : ap,
                  },
                })
              }
            />
          </FilterContainer>
        )}
        {filtersById.region && (
          <FilterContainer>
            <MultiRegionSelect
              values={reportFilters.region || []}
              onChange={(regions) => {
                if (reportFilters.region && regions.length > reportFilters.region.length) {
                  track.reportPage({
                    eventName: track.Event.FILTER,
                    reportName,
                    props: {
                      filterName: 'region',
                      filterValue: regions,
                    },
                  })
                } else {
                  track.reportPage({
                    eventName: track.Event.CLEAR_FILTER,
                    reportName,
                    props: {
                      filterName: 'region',
                      filterValue: regions,
                    },
                  })
                }

                dispatchFilter({
                  type: IActionType.FILTER,
                  payload: {
                    category: 'region',
                    value: regions,
                  },
                })
              }}
            />
          </FilterContainer>
        )}
        {filtersById.country && (
          <FilterContainer>
            <MultiCountrySelect
              values={reportFilters.country ? reportFilters.country : []}
              onChange={(countries) => {
                if (reportFilters.country && countries.length > reportFilters.country.length) {
                  track.reportPage({
                    eventName: track.Event.FILTER,
                    reportName,
                    props: {
                      filterName: 'country',
                      filterValue: countries,
                    },
                  })
                } else {
                  track.reportPage({
                    eventName: track.Event.CLEAR_FILTER,
                    reportName,
                    props: {
                      filterName: 'country',
                      filterValue: countries,
                    },
                  })
                }

                dispatchFilter({
                  type: IActionType.FILTER,
                  payload: {
                    category: 'country',
                    value: countries,
                  },
                })
              }}
            />
          </FilterContainer>
        )}
        {filtersById.buildingId && (
          <FilterContainer>
            <MultiLocationSelect
              includeOldBuildings={reportConfig.includeOldBuildings}
              mode={mode}
              values={
                locationType === 'building'
                  ? reportFilters.buildingId || []
                  : reportFilters.floorId || []
              }
              onChange={(newSelection) => {
                if (locationType === 'building') {
                  if (
                    reportFilters.buildingId &&
                    newSelection.length > reportFilters.buildingId.length
                  ) {
                    track.reportPage({
                      eventName: track.Event.FILTER,
                      reportName,
                      props: {
                        filterName: 'buildingId',
                        filterValue: newSelection,
                      },
                    })
                  } else {
                    track.reportPage({
                      eventName: track.Event.CLEAR_FILTER,
                      reportName,
                      props: {
                        filterName: 'buildingId',
                        filterValue: newSelection,
                      },
                    })
                  }
                } else {
                  if (reportFilters.floorId && newSelection.length > reportFilters.floorId.length) {
                    track.reportPage({
                      eventName: track.Event.FILTER,
                      reportName,
                      props: {
                        filterName: 'floorId',
                        filterValue: newSelection,
                      },
                    })
                  } else {
                    track.reportPage({
                      eventName: track.Event.CLEAR_FILTER,
                      reportName,
                      props: {
                        filterName: 'floorId',
                        filterValue: newSelection,
                      },
                    })
                  }
                }

                dispatchFilter({
                  type: IActionType.FILTER,
                  payload: {
                    category: locationType === 'building' ? 'buildingId' : 'floorId',
                    value: newSelection,
                  },
                })
              }}
            />
          </FilterContainer>
        )}
        {filtersById.departmentId && (
          <FilterContainer>
            <MultiDepartmentSelect
              values={reportFilters.departmentId || []}
              title="Employee Departments"
              onChange={(departments) => {
                if (
                  reportFilters.departmentId &&
                  departments.length > reportFilters.departmentId.length
                ) {
                  track.reportPage({
                    eventName: track.Event.FILTER,
                    reportName,
                    props: {
                      filterName: 'departmentId',
                      filterValue: departments,
                    },
                  })
                } else {
                  track.reportPage({
                    eventName: track.Event.CLEAR_FILTER,
                    reportName,
                    props: {
                      filterName: 'departmentId',
                      filterValue: departments,
                    },
                  })
                }

                dispatchFilter({
                  type: IActionType.FILTER,
                  payload: {
                    category: 'departmentId',
                    value: departments,
                  },
                })
              }}
            />
          </FilterContainer>
        )}
        {filtersById.spaceTypeId && (
          <FilterContainer>
            <MultiSpaceTypeSelect
              values={reportFilters.spaceTypeId || []}
              onChange={(spaceTypes) => {
                if (
                  reportFilters.spaceTypeId &&
                  spaceTypes.length > reportFilters.spaceTypeId.length
                ) {
                  track.reportPage({
                    eventName: track.Event.FILTER,
                    reportName,
                    props: {
                      filterName: 'spaceTypeId',
                      filterValue: spaceTypes,
                    },
                  })
                } else {
                  track.reportPage({
                    eventName: track.Event.CLEAR_FILTER,
                    reportName,
                    props: {
                      filterName: 'spaceTypeId',
                      filterValue: spaceTypes,
                    },
                  })
                }

                dispatchFilter({
                  type: IActionType.FILTER,
                  payload: {
                    category: 'spaceTypeId',
                    value: spaceTypes,
                  },
                })
              }}
            />
          </FilterContainer>
        )}
        {filtersById.site && (
          <FilterContainer>
            <MultiHRLocationSelect
              values={reportFilters.site || []}
              onChange={(hrLocations) => {
                if (reportFilters.site && hrLocations.length > reportFilters.site.length) {
                  track.reportPage({
                    eventName: track.Event.FILTER,
                    reportName,
                    props: {
                      filterName: 'site',
                      filterValue: hrLocations,
                    },
                  })
                } else {
                  track.reportPage({
                    eventName: track.Event.CLEAR_FILTER,
                    reportName,
                    props: {
                      filterName: 'site',
                      filterValue: hrLocations,
                    },
                  })
                }

                dispatchFilter({
                  type: IActionType.FILTER,
                  payload: {
                    category: 'site',
                    value: hrLocations,
                  },
                })
              }}
            />
          </FilterContainer>
        )}
        {filtersById.usageTypeId && (
          <FilterContainer>
            <MultiUsageTypeSelect
              values={reportFilters.usageTypeId || []}
              onChange={(usageTypes) => {
                if (
                  reportFilters.usageTypeId &&
                  usageTypes.length > reportFilters.usageTypeId.length
                ) {
                  track.reportPage({
                    eventName: track.Event.FILTER,
                    reportName,
                    props: {
                      filterName: 'usageTypeId',
                      filterValue: usageTypes,
                    },
                  })
                } else {
                  track.reportPage({
                    eventName: track.Event.CLEAR_FILTER,
                    reportName,
                    props: {
                      filterName: 'usageTypeId',
                      filterValue: usageTypes,
                    },
                  })
                }

                dispatchFilter({
                  type: IActionType.FILTER,
                  payload: {
                    category: 'usageTypeId',
                    value: usageTypes,
                  },
                })
              }}
            />
          </FilterContainer>
        )}
        {filtersById.employmentType && (
          <FilterContainer>
            <MultiEmployeeTypeSelect
              values={reportFilters.employmentType || []}
              onChange={(employeeTypes) => {
                if (
                  reportFilters.employmentType &&
                  employeeTypes.length > reportFilters.employmentType.length
                ) {
                  track.reportPage({
                    eventName: track.Event.FILTER,
                    reportName,
                    props: {
                      filterName: 'employmentType',
                      filterValue: employeeTypes,
                    },
                  })
                } else {
                  track.reportPage({
                    eventName: track.Event.CLEAR_FILTER,
                    reportName,
                    props: {
                      filterName: 'employmentType',
                      filterValue: employeeTypes,
                    },
                  })
                }

                dispatchFilter({
                  type: IActionType.FILTER,
                  payload: {
                    category: 'employmentType',
                    value: employeeTypes,
                  },
                })
              }}
            />
          </FilterContainer>
        )}
        {filtersById.globalMapObjectId && (
          <FilterContainer>
            <MultiGlobalMapObjectSelect
              values={reportFilters.globalMapObjectId || []}
              onChange={(mapObjects) => {
                if (
                  reportFilters.globalMapObjectId &&
                  mapObjects.length > reportFilters.globalMapObjectId.length
                ) {
                  track.reportPage({
                    eventName: track.Event.FILTER,
                    reportName,
                    props: {
                      filterName: 'globalMapObjectId',
                      filterValue: mapObjects,
                    },
                  })
                } else {
                  track.reportPage({
                    eventName: track.Event.CLEAR_FILTER,
                    reportName,
                    props: {
                      filterName: 'globalMapObjectId',
                      filterValue: mapObjects,
                    },
                  })
                }

                dispatchFilter({
                  type: IActionType.FILTER,
                  payload: {
                    category: 'globalMapObjectId',
                    value: mapObjects,
                  },
                })
              }}
            />
          </FilterContainer>
        )}
        {filtersById.costCenterId && (
          <FilterContainer>
            <MultiCostCenterSelect
              values={reportFilters.costCenterId || []}
              onChange={(costCenters) => {
                if (
                  reportFilters.costCenterId &&
                  costCenters.length > reportFilters.costCenterId.length
                ) {
                  track.reportPage({
                    eventName: track.Event.FILTER,
                    reportName,
                    props: {
                      filterName: 'costCenterId',
                      filterValue: costCenters,
                    },
                  })
                } else {
                  track.reportPage({
                    eventName: track.Event.CLEAR_FILTER,
                    reportName,
                    props: {
                      filterName: 'costCenterId',
                      filterValue: costCenters,
                    },
                  })
                }

                dispatchFilter({
                  type: IActionType.FILTER,
                  payload: {
                    category: 'costCenterId',
                    value: costCenters,
                  },
                })
              }}
            />
          </FilterContainer>
        )}
        {filtersById.size && (
          <FilterContainer>
            <MultiSizeSelect
              values={reportFilters.size || []}
              onChange={(sizes) => {
                if (reportFilters.size && sizes.length > reportFilters.size.length) {
                  track.reportPage({
                    eventName: track.Event.FILTER,
                    reportName,
                    props: {
                      filterName: 'size',
                      filterValue: sizes,
                    },
                  })
                } else {
                  track.reportPage({
                    eventName: track.Event.CLEAR_FILTER,
                    reportName,
                    props: {
                      filterName: 'size',
                      filterValue: sizes,
                    },
                  })
                }

                dispatchFilter({
                  type: IActionType.FILTER,
                  payload: {
                    category: 'size',
                    value: sizes,
                  },
                })
              }}
            />
          </FilterContainer>
        )}
        {filtersById.employeeId && (
          <FilterContainer>
            <MultiEmployeeSelect
              title="Booked For"
              reselectAllItems={false}
              values={reportFilters?.employees || []}
              onChange={(employees) => {
                if (reportFilters.employees && employees.length > reportFilters.employees.length) {
                  track.reportPage({
                    eventName: track.Event.FILTER,
                    reportName,
                    props: {
                      filterName: 'employeeId',
                      filterValue: employees,
                    },
                  })
                } else {
                  track.reportPage({
                    eventName: track.Event.CLEAR_FILTER,
                    reportName,
                    props: {
                      filterName: 'employeeId',
                      filterValue: employees,
                    },
                  })
                }

                dispatchFilter({
                  type: IActionType.FILTER,
                  payload: {
                    category: 'employees',
                    value: employees,
                  },
                })
              }}
            />
          </FilterContainer>
        )}
      </Flex>
    </div>
  )
}
